import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useDealerDuck } from '@hooks/useDealerDuck';
import { useDispatch, useSelector } from 'react-redux';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { BasketFeesDetailsProps } from '@components/v2/BasketFeesDetails/index';
import BasketSelectSection from '@components/v2/BasketSelectSection';
import { POSTCODE_REGEX } from '@utils/checkoutInputsValidation';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import {
    BASKET_STEPS,
    isBrandAC,
    MALUS_FEE,
    REGISTRATION_FEE,
    REGISTRATION_FEES_VALUES,
    ModalVersion,
    isBrandDS,
} from 'src/constants/main';
import { IDealerFee } from '@components/DealerFees/DealerFeesTemplate';
import { useCarteGrise } from '@hooks/useCarteGrise';
import { GtmData } from 'src/types/gtm';
import { useGTM } from '@hooks/useGTM';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { theme } from 'src/styles/theme';
import Spinner from '@components/Spinner';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import { Trans } from 'src/../i18n';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import { getCurrentCulture } from '@utils/getCurrentCulture';
import { updateBasketSteps } from '@utils/Car.utils';
import { dealerApi } from 'src/services';
import isEmpty from 'lodash/isEmpty';
import Button from '@components/Button';
import { PERSONALIZE_OFFER_SECTIONS } from '@components/Modal/ModalPersonalizeOffer/ModalPersonalizeOfferTemplate';
import { IconTemplate } from '@components/Icon/IconTemplate';
import { validateDealerId } from '@utils/Deal.utils';
import { useUserDuck } from '@hooks/useUserDuck';

const BasketFeesDetailsTemplate: FC<BasketFeesDetailsProps> = ({ className, generatedIndex }) => {
    const inputRef = useRef<HTMLInputElement>();
    const { t, i18n } = useTranslations();
    const dispatch = useDispatch();
    const { formatMoney } = useMoneyFormatter();
    const { currentDeal } = useCarDetailsDuck();
    const { carteGriseError } = useGlobalDuck();
    const { updateCarteGrise } = useCarteGrise();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { pushToDataLayer } = useGTM();
    const isPopulateCarteGrise = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_POPULATE_CARTE_GRISE);
    const isCarteGriseEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_CARTE_GRISE_ENABLED);
    const [isOpen, setIsOpen] = useState(true);

    const { currentCulture } = getCurrentCulture(i18n);

    const { dealer } = useDealerDuck();

    const carteGriseIsLoading = useSelector((state) => UIDuck.getOwnState(state).carteGriseLoading);

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );
    const carteGriseDealerFee = currentDeal?.extraFields?.carteGrise
        ? JSON.parse(currentDeal?.extraFields?.carteGrise)
        : {};
    const malusFee = dealerFees?.find((dealerFee: IDealerFee) => dealerFee.id === MALUS_FEE);
    const registrationFee = dealerFees?.find((dealerFee: IDealerFee) => dealerFee.id === REGISTRATION_FEE);

    const [isInvalid, setIsInvalid] = useState(false);
    const [postalCode, setPostalCode] = useState(currentDeal?.extraFields?.postalCode);
    const [modifyPostalCode, setModifyPostalCode] = useState(false);

    const selectedFeesChoice = currentDeal?.extraFields?.selectedFeesValue;
    const isCarteGriseEnabledAndApplied = isCarteGriseEnabled && currentDeal.extraFields?.postalCode;
    const validatePostalCode = async (code?: string) => {
        const postOfficeCode = code || inputRef?.current?.value || '';
        const isValid = POSTCODE_REGEX.test(postOfficeCode);

        if (isValid) {
            setIsInvalid(false);
            setPostalCode(postOfficeCode);
            await updateCarteGrise(postOfficeCode, selectedFeesChoice === REGISTRATION_FEES_VALUES.YES);
            pushToDataLayer({
                eventCategory: 'SolRetail::Basket',
                eventAction: 'Validate::ZipCode',
                eventLabel: t(postOfficeCode ? 'checkout.button.editDetails' : 'basket.dealerFee.carteGrise.button'),
            } as GtmData);
            setModifyPostalCode(false);
            return;
        }

        setIsInvalid(true);
    };

    const selectFeesChoice = async (value: string) => {
        setModifyPostalCode(false);
        await updateBasketSteps(dispatch, currentDeal, BASKET_STEPS.FEES, value);
        if (postalCode) {
            await updateCarteGrise(postalCode, value === REGISTRATION_FEES_VALUES.YES);
        }
    };

    useEffect(() => {
        const handleServiceError = () => {
            return dispatch(
                UIDuck.openModal({
                    data: { data: {} },
                    callbacks: {},
                    modalType: UIDuck.MODAL_TYPES.DEALER_FEES_POSTAL_CODE_ERROR,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );
        };

        if (carteGriseError) {
            handleServiceError();
            updateBasketSteps(dispatch, currentDeal, BASKET_STEPS.FEES, null);
        }
    }, [carteGriseError]);

    const populateDealerPostalCodeCarteGrise = async () => {
        const { data } = await dealerApi.getDealer(currentDeal.dealerId);
        validatePostalCode(data.postCode);
    };

    useEffect(() => {
        setPostalCode(currentDeal?.extraFields?.postalCode);
    }, [currentDeal]);

    useEffect(() => {
        if (isCarteGriseEnabled && isPopulateCarteGrise) {
            if (dealer?.postCode) {
                validatePostalCode(dealer.postCode);
                return;
            }

            if (!dealer?.postCode && validateDealerId(currentDeal?.dealerId)) {
                populateDealerPostalCodeCarteGrise();
                return;
            }

            const code = currentDeal?.fullProductConfiguration?.dealers?.[0]?.address?.[currentCulture]?.zipCode;
            if (code) {
                validatePostalCode(code);
                return;
            }
        }
    }, []);

    const postalCodeInputProps = pickBy(
        {
            ref: inputRef,
            className: `${selectedFeesChoice === REGISTRATION_FEES_VALUES.YES && !postalCode ? 'warning' : ''}`,
            placeholder: t('basket.fees.carteGrise.input'),
            defaultValue: Boolean(isCarteGriseEnabled && isPopulateCarteGrise) && postalCode,
        },
        identity
    );

    return (
        <div id="fees" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.fees.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                {isCarteGriseEnabled && (
                    <div className="title">
                        <span>{t('basket.fees.carteGrise.title')}</span>
                    </div>
                )}
                {isCarteGriseEnabled && (
                    <div className="carteGriseInput">
                        <div className="description">
                            <p>{t('basket.fees.carteGrise.description')}</p>
                        </div>
                        {currentDeal?.extraFields?.postalCode && !modifyPostalCode ? (
                            <div className="summary">
                                <p className="item">
                                    <span>{t('basket.fees.carteGrise.summary')}</span>
                                    <span className="price">
                                        {carteGriseIsLoading ? (
                                            <Spinner size={16} border={3} color={theme.colors.black} />
                                        ) : (
                                            postalCode
                                        )}
                                    </span>
                                </p>
                                <p className="item noBorderTop">
                                    <span>{t('basket.fees.carteGrise.title')}</span>
                                    <span className="price" data-testid="TESTING_POSTAL_CODE_PRICE">
                                        {carteGriseIsLoading ? (
                                            <Spinner size={16} border={3} color={theme.colors.black} />
                                        ) : (
                                            formatMoney(carteGriseDealerFee?.priceTotal?.priceTTC)
                                        )}
                                    </span>
                                </p>
                                <p className="item noBorderTop">
                                    <button className="item-link" onClick={() => setModifyPostalCode(true)}>
                                        {t('basket.fees.carteGrise.modify')}
                                    </button>
                                    <span />
                                </p>
                            </div>
                        ) : (
                            <div className="postalCode">
                                <span className="postalCode_title">{t('basket.fees.carteGrise.input.title')}</span>
                                {selectedFeesChoice === REGISTRATION_FEES_VALUES.YES && !postalCode && (
                                    <div className="postalCode_warning">
                                        <Icon
                                            width={35}
                                            height={35}
                                            name={isBrandAC ? Icons.AlertOutlined : Icons.Alert}
                                        />
                                        <span>{t('basket.fees.carteGrise.input.alert')}</span>
                                    </div>
                                )}
                                <div className="postalCode_input">
                                    <input data-testid="TESTING_POSTAL_CODE_INPUT" {...postalCodeInputProps} />
                                    {isInvalid && (
                                        <span className="error">
                                            {t('basket.dealerFee.carteGrise.invalidPostalCode')}
                                        </span>
                                    )}
                                    <button
                                        data-testid="TESTING_POSTAL_CODE_BUTTON"
                                        onClick={() => validatePostalCode()}
                                    >
                                        {carteGriseIsLoading ? (
                                            <Spinner
                                                size={20}
                                                border={3}
                                                color={isBrandDS ? theme.colors.black : theme.colors.white}
                                            />
                                        ) : (
                                            t('basket.fees.carteGrise.input.button')
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="registrationFees">
                    <div className="title">
                        <span>{t('basket.fees.registration.title')}</span>
                    </div>
                    <div className="description">
                        <p>{t('basket.fees.registration.description')}</p>
                    </div>
                    <BasketSelectSection
                        leftSectionTitle={t('basket.fees.noInclude')}
                        leftSectionDescription={t('basket.fees.noInclude.description')}
                        rightSectionTitle={t('basket.fees.include')}
                        rightSectionDescription={t('basket.fees.include.description')}
                        leftSelected={selectedFeesChoice === REGISTRATION_FEES_VALUES.NO}
                        rightSelected={selectedFeesChoice === REGISTRATION_FEES_VALUES.YES}
                        onClick={selectFeesChoice}
                        leftValue={REGISTRATION_FEES_VALUES.NO}
                        rightValue={REGISTRATION_FEES_VALUES.YES}
                    />
                </div>
                <div className="carteGriseSummary">
                    <div className="description">
                        {isCarteGriseEnabled && (
                            <Trans i18nKey="basket.fees.carteGrise.summary.description" t={t}>
                                <p>{t('basket.fees.carteGrise.summary.description')}</p>
                            </Trans>
                        )}
                    </div>
                    <div className="summary">
                        {registrationFee && (
                            <p className="item">
                                <span>{t(registrationFee.title)}</span>
                                <span className="price">{formatMoney(registrationFee.priceInclTax, true)}</span>
                            </p>
                        )}
                    </div>
                </div>
                {isCarteGriseEnabledAndApplied && selectedFeesChoice === REGISTRATION_FEES_VALUES.NO && (
                    <div className="carteGriseInformation">
                        <Trans i18nKey="basket.noCarteGriseIncluded" t={t}>
                            <span>{t('basket.fees.carteGrise.noIncluded')}</span>
                        </Trans>
                    </div>
                )}
                {malusFee && (
                    <div className="malusFees">
                        <div className="title">
                            <span>{t('basket.fees.malus.title')}</span>
                        </div>
                        <div className="malusFeesSummary">
                            <div className="description">
                                <p>{t('basket.fees.malus.description')}</p>
                            </div>
                            <div className="summary">
                                <p className="item">
                                    <span>{t(malusFee.title)}</span>
                                    <span className="price" data-testid="TESTING_MALUS_FEE_PRICE">
                                        {formatMoney(malusFee.priceInclTax, true)}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {isUserLoggedInAsDealer && (
                    <Button
                        onClick={() => {
                            pushToDataLayer({
                                event: 'uaevent',
                                eventCategory: 'Content',
                                eventAction: 'Display::EditConfigurationModal',
                                eventLabel: t('modal.personalizeOffer.edit.button'),
                            });
                            dispatch(
                                UIDuck.openModal({
                                    data: {
                                        openSection: PERSONALIZE_OFFER_SECTIONS.REGISTRATION_FEES,
                                    },
                                    modalType: UIDuck.MODAL_TYPES.PERSONALIZE_OFFER,
                                    modalVersion: ModalVersion.v2,
                                    modalProperties: {
                                        withoutPadding: true,
                                    },
                                })
                            );
                        }}
                        secondary
                        fitContentWidth
                        centerMode
                        hasEditIcon
                        marginTop={40}
                        marginBottom={20}
                    >
                        <IconTemplate name={Icons.edit} />
                        {t('modal.personalizeOffer.edit.button')}
                    </Button>
                )}
            </Collapsible>
        </div>
    );
};

export default BasketFeesDetailsTemplate;
